/**
 * Zebedo Cart Drawer - Complete Rewrite
 * Self-contained cart drawer with direct API calls
 * No external dependencies on CartItems or ZebedoQuantityHandler
 */

window.zebedoOpenCart = function (triggeredBy) {
  const themeDrawer = document.querySelector("zebedo-cart-drawer");

  const openThemeDrawer = () => {
    document.documentElement.removeAttribute("data-cart-drawer");
    if (themeDrawer && typeof themeDrawer.open === "function") {
      themeDrawer.open(triggeredBy);
      return;
    }
    window.location.href = window.routes?.cart_url || "/cart";
  };

  if (!window.ZEBEDO_USE_OCU_CART) {
    openThemeDrawer();
    return;
  }

  window.dispatchEvent(new CustomEvent("ocu:cart:open"));

  window.setTimeout(() => {
    if (window.zebedoIsOcuCartOpen && window.zebedoIsOcuCartOpen()) return;
    openThemeDrawer();
  }, 350);
};

window.zebedoIsOcuCartOpen = function () {
  const selectors = [
    '[class*="ocu-slide"]',
    '[class*="OCU"]',
    '[id*="ocu-slide"]',
    '[id*="OCU"]',
    '[data-ocu-cart-drawer]',
  ];

  return selectors.some((selector) => {
    const nodes = document.querySelectorAll(selector);
    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden") continue;
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return true;
    }
    return false;
  });
};

class ZebedoCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.setupEventDelegation();
    this.bindCartIconWhenReady();
    setTimeout(() => this.updateShippingProgress(), 0);
  }

  bindCartIconWhenReady() {
    const bind = () => this.setHeaderCartIconAccessibility();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bind, { once: true });
    } else {
      bind();
    }
  }

  /**
   * Single event delegation handler for ALL cart drawer interactions
   * This ensures events work even after DOM updates
   */
  setupEventDelegation() {
    // Only attach once
    if (this.dataset.delegationAttached) return;
    this.dataset.delegationAttached = "true";

    this.addEventListener("click", (e) => {
      // Close button
      if (e.target.closest(".zebedo-drawer__close")) {
        e.preventDefault();
        this.close();
        return;
      }

      // Overlay click to close
      if (e.target.id === "ZebedoCartDrawer-Overlay") {
        this.close();
        return;
      }

      // Quantity minus button
      const minusBtn = e.target.closest('.quantity__button[name="minus"]');
      if (minusBtn) {
        e.preventDefault();
        this.handleQuantityChange(minusBtn, -1);
        return;
      }

      // Quantity plus button
      const plusBtn = e.target.closest('.quantity__button[name="plus"]');
      if (plusBtn) {
        e.preventDefault();
        this.handleQuantityChange(plusBtn, 1);
        return;
      }

      // Remove button
      const removeBtn = e.target.closest(".zebedo-item-remove");
      if (removeBtn) {
        e.preventDefault();
        const index = removeBtn.dataset.index;
        if (index) this.updateCartQuantity(parseInt(index), 0);
        return;
      }
    });

    // Handle direct input changes
    this.addEventListener("change", (e) => {
      if (e.target.classList.contains("quantity__input")) {
        const input = e.target;
        const index = parseInt(input.dataset.index);
        const quantity = parseInt(input.value);
        if (!isNaN(index) && !isNaN(quantity)) {
          this.updateCartQuantity(index, quantity);
        }
      }
    });
  }

  /**
   * Handle +/- button clicks
   */
  handleQuantityChange(button, delta) {
    if (this.isUpdating) return;

    const quantityContainer = button.closest(".quantity");
    const input = quantityContainer?.querySelector(".quantity__input");
    if (!input) return;

    const currentValue = parseInt(input.value) || 0;
    const step = parseInt(input.step) || 1;
    const min = parseInt(input.min) || 1;
    const max = input.max ? parseInt(input.max) : 9999;
    const index = parseInt(input.dataset.index);

    let newValue = currentValue + delta * step;
    newValue = Math.max(min, Math.min(max, newValue));

    // If going below min, treat as remove
    if (newValue < min) newValue = 0;

    if (newValue !== currentValue && !isNaN(index)) {
      input.value = newValue;
      this.updateCartQuantity(index, newValue);
    }
  }

  /**
   * Core cart update function - makes API call and refreshes drawer
   */
  updateCartQuantity(lineIndex, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    // Show loading state
    this.showLoading();

    const cartChangeUrl = window.routes?.cart_change_url || "/cart/change.js";

    fetch(cartChangeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        line: lineIndex,
        quantity: quantity,
        sections: ["zebedo-cart-drawer"],
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        console.log("[ZebedoCart] Cart updated:", cart);

        // Update the drawer HTML from section rendering
        if (cart.sections && cart.sections["zebedo-cart-drawer"]) {
          this.replaceDrawerContent(cart.sections["zebedo-cart-drawer"]);
        }

        // Update header cart count
        this.updateHeaderCartCount(cart.item_count);

        // Handle empty cart
        if (cart.item_count === 0) {
          this.classList.add("is-empty");
        } else {
          this.classList.remove("is-empty");
        }
      })
      .catch((error) => {
        console.error("[ZebedoCart] Update failed:", error);
        // Refresh the drawer from server to reset state
        this.refreshFromServer();
      })
      .finally(() => {
        this.isUpdating = false;
        this.hideLoading();
      });
  }

  /**
   * Replace drawer content with new HTML from Shopify section rendering
   */
  replaceDrawerContent(sectionHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, "text/html");

    // Replace the entire #ZebedoCartDrawer content
    const newContent = doc.querySelector("#ZebedoCartDrawer");
    const currentContent = this.querySelector("#ZebedoCartDrawer");

    if (newContent && currentContent) {
      currentContent.innerHTML = newContent.innerHTML;
      this.updateShippingProgress();
      console.log("[ZebedoCart] Drawer content replaced");
    } else {
      console.warn("[ZebedoCart] Could not find #ZebedoCartDrawer in response");
    }
  }

  /**
   * Ensure the shipping progress bar fills correctly
   */
  updateShippingProgress() {
    const fill = this.querySelector(".zebedo-progress-fill");
    if (!fill) return;

    const progressValue = parseFloat(fill.getAttribute("data-percent"));
    if (!Number.isFinite(progressValue)) return;

    const clamped = Math.max(0, Math.min(100, progressValue));
    fill.style.width = `${clamped}%`;
  }

  /**
   * Fallback: Refresh entire drawer from server
   */
  refreshFromServer() {
    const cartUrl = window.routes?.cart_url || "/cart";
    fetch(`${cartUrl}?section_id=zebedo-cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        this.replaceDrawerContent(html);
      })
      .catch(console.error);
  }

  /**
   * Update header cart count badge (single badge — zebedo style only)
   */
  updateHeaderCartCount(count) {
    if (typeof window.ZebedoCart?.updateCartIconCount === "function") {
      window.ZebedoCart.updateCartIconCount(count);
      return;
    }

    const cartLink = document.querySelector("#cart-icon-bubble");

    if (cartLink) {
      cartLink.querySelectorAll(".cart-count-bubble").forEach((el) => el.remove());

      let badge = cartLink.querySelector(".zebedo-header__cart-count");

      if (count > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "zebedo-header__cart-count";
          badge.setAttribute("aria-live", "polite");
          cartLink.appendChild(badge);
        }
        badge.textContent = count;
        badge.hidden = false;
        badge.style.display = "";
      } else if (badge) {
        badge.hidden = true;
        badge.style.display = "none";
      }
    }
  }

  /**
   * Show loading overlay
   */
  showLoading() {
    this.classList.add("zebedo-drawer--loading");
    const overlay = this.querySelector(".loading-overlay");
    if (overlay) overlay.classList.remove("hidden");
  }

  /**
   * Hide loading overlay
   */
  hideLoading() {
    this.classList.remove("zebedo-drawer--loading");
    const overlay = this.querySelector(".loading-overlay");
    if (overlay) overlay.classList.add("hidden");
  }

  /**
   * Set up cart icon in header to open drawer
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink || cartLink.dataset.zebedoCartBound === "true") return;

    cartLink.dataset.zebedoCartBound = "true";
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    const openDrawer = (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.zebedoOpenCart(cartLink);
    };

    cartLink.addEventListener("click", openDrawer);

    cartLink.addEventListener("keydown", (event) => {
      if (event.code === "Space" || event.code === "Enter") {
        event.preventDefault();
        window.zebedoOpenCart(cartLink);
      }
    });
  }

  /**
   * Open the drawer
   */
  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    this.classList.add("active");
    document.body.classList.add("overflow-hidden");

    setTimeout(() => {
      this.classList.add("animate");
      const focusEl = this.querySelector(".zebedo-drawer__close");
      if (focusEl) focusEl.focus();
    }, 10);
  }

  /**
   * Close the drawer
   */
  close() {
    this.classList.remove("active", "animate");
    document.body.classList.remove("overflow-hidden");

    if (this.activeElement) {
      this.activeElement.focus();
      this.activeElement = null;
    }
  }

  /**
   * Set active element for focus management (called by product forms)
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Render contents after add-to-cart (called by product forms)
   */
  renderContents(parsedState) {
    this.updateHeaderCartCount(parsedState.item_count);
    this.classList.toggle("is-empty", parsedState.item_count === 0);

    if (parsedState.sections && parsedState.sections["zebedo-cart-drawer"]) {
      this.replaceDrawerContent(parsedState.sections["zebedo-cart-drawer"]);
    }

    window.zebedoOpenCart(this.activeElement);
  }

  /**
   * Get sections to render (for compatibility with add-to-cart)
   */
  getSectionsToRender() {
    return [{ id: "zebedo-cart-drawer", selector: "#ZebedoCartDrawer" }];
  }
}

customElements.define("zebedo-cart-drawer", ZebedoCartDrawer);

// Global remove function for onclick handlers in Liquid
window.zebedoRemoveItem = function (index) {
  const drawer = document.querySelector("zebedo-cart-drawer");
  if (drawer) {
    drawer.updateCartQuantity(parseInt(index), 0);
  }
};
